import { useEffect, useState, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Plus, Trash2, Loader2, BookOpen, UploadCloud, RefreshCw, Image as ImageIcon,
  Pencil, Check, X, ChevronUp, ChevronDown, FileText, MapPin, Layers,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  catalogueService,
  type Catalogue,
  type BrandOption,
} from '../../services/catalogueService';

// Native button styles (the app's <Button> is react-aria based; native buttons keep
// onClick/disabled/title simple here). Kept in sync with BrandsPanel.
const BTN = 'inline-flex items-center justify-center gap-1.5 rounded-md border text-sm font-medium transition-colors disabled:opacity-50 disabled:pointer-events-none';
const btnPrimary = `${BTN} h-9 px-3 border-transparent bg-primary text-primary-foreground hover:opacity-90`;
const btnPrimarySm = `${BTN} h-8 px-2.5 border-transparent bg-primary text-primary-foreground hover:opacity-90`;
const btnOutline = `${BTN} h-9 px-3 border-border bg-transparent hover:bg-muted`;
const btnOutlineSm = `${BTN} h-8 px-2.5 border-border bg-transparent hover:bg-muted`;
const btnGhostSm = `${BTN} h-8 px-2 border-transparent bg-transparent hover:bg-muted`;
const btnDangerSm = `${BTN} h-8 px-2.5 border-destructive/40 bg-destructive/10 text-destructive hover:bg-destructive/20`;
const inputCls = 'h-9 w-full rounded-md border border-input bg-background px-2.5 text-sm';

function fmtSize(mb: number) {
  if (!mb) return '—';
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.round(mb * 1024)} KB`;
}

// ── Small modal ───────────────────────────────────────────────────────────────
function Modal({ open, onClose, title, children }: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            className="relative z-10 w-full max-w-md rounded-xl border border-border bg-card p-5 shadow-xl"
            initial={{ scale: 0.95, y: 8 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 8 }}
            transition={{ type: 'spring', stiffness: 300, damping: 26 }}
          >
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-base font-semibold text-foreground">{title}</h3>
              <button type="button" className={btnGhostSm} onClick={onClose}><X className="h-4 w-4" /></button>
            </div>
            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ── Extract-positions dialog ────────────────────────────────────────────────────
function ExtractDialog({ catalogue, onDone, onError }: {
  catalogue: Catalogue;
  onDone: (msg: string) => void;
  onError: (msg: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [pages, setPages] = useState<number | null>(null);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [dryRun, setDryRun] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);

  const openDialog = async () => {
    setOpen(true);
    setPages(null);
    try {
      setPages(await catalogueService.pageCount(catalogue.id));
    } catch { /* page count is best-effort */ }
  };

  const run = async () => {
    setBusy(true);
    setProgress(null);
    try {
      const res = await catalogueService.runExtraction(
        catalogue.id,
        {
          startPage: start ? Number(start) : undefined,
          endPage: end ? Number(end) : undefined,
          dryRun,
        },
        (job) => {
          if (job.progress) setProgress(`Page ${job.progress.page} of ${job.progress.endPage} · ${job.progress.matches} matches so far`);
        }
      );
      setOpen(false);
      const extra = [
        res.ambiguousSkus ? `${res.ambiguousSkus} colliding SKUs skipped` : null,
        res.unmatchedSkus ? `${res.unmatchedSkus} SKUs not found` : null,
      ].filter(Boolean).join(', ');
      onDone(
        `${catalogue.name}: ${res.dryRun ? 'would map' : 'mapped'} ${res.matches} positions across ${res.products} products` +
          ` (${res.scope === 'all' ? 'all pages' : `pages ${res.scope}`})${extra ? ` — ${extra}` : ''}`
      );
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  return (
    <>
      <button type="button" className={btnOutlineSm} onClick={openDialog} title="Match SKUs to catalogue pages">
        <MapPin className="h-4 w-4" /> Map pages
      </button>
      <Modal open={open} onClose={() => !busy && setOpen(false)} title="Map products to pages">
        <p className="mb-3 text-sm text-muted-foreground">
          Scans the PDF text for each active <span className="text-foreground">{catalogue.brand}</span> SKU and
          records the page + position so the interactive catalogue can place hotspots.
          {pages !== null && <> This PDF has <span className="text-foreground">{pages}</span> pages.</>}
        </p>
        <div className="mb-3 flex items-center gap-2">
          <div className="flex-1">
            <label className="text-xs text-muted-foreground">Start page</label>
            <input value={start} onChange={(e) => setStart(e.target.value.replace(/\D/g, ''))} placeholder="1" className={inputCls} inputMode="numeric" />
          </div>
          <div className="flex-1">
            <label className="text-xs text-muted-foreground">End page</label>
            <input value={end} onChange={(e) => setEnd(e.target.value.replace(/\D/g, ''))} placeholder={pages ? String(pages) : 'last'} className={inputCls} inputMode="numeric" />
          </div>
        </div>
        <p className="mb-3 text-xs text-muted-foreground">
          A full run replaces every position for this catalogue; a page range replaces only those pages.
          Nothing is removed until parsing succeeds, so a failed run keeps the current mapping.
        </p>
        <label className="mb-4 flex items-center gap-2 text-sm text-foreground">
          <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />
          Dry run (report counts, write nothing)
        </label>
        {progress && <p className="mb-3 text-xs text-muted-foreground">{progress}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" className={btnOutline} onClick={() => setOpen(false)} disabled={busy}>Cancel</button>
          <button type="button" className={btnPrimary} onClick={run} disabled={busy}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />} Run mapping
          </button>
        </div>
      </Modal>
    </>
  );
}

// ── Catalogue card ───────────────────────────────────────────────────────────────
function CatalogueCard({ catalogue, isFirst, isLast, onChanged, onNotice, onError, onMove }: {
  catalogue: Catalogue;
  isFirst: boolean;
  isLast: boolean;
  onChanged: () => void;
  onNotice: (msg: string) => void;
  onError: (msg: string) => void;
  onMove: (dir: -1 | 1) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ name: catalogue.name, brand: catalogue.brand, description: catalogue.description });
  const [busy, setBusy] = useState<null | 'save' | 'cover' | 'delete'>(null);

  const save = async () => {
    if (!draft.name.trim() || !draft.brand.trim()) return onError('Name and brand are required');
    setBusy('save');
    try {
      await catalogueService.update(catalogue.id, draft);
      setEditing(false);
      onNotice(`Updated "${draft.name}"`);
      onChanged();
    } catch (e) { onError((e as Error).message); } finally { setBusy(null); }
  };

  const extractCover = async () => {
    setBusy('cover');
    try {
      await catalogueService.extractCover(catalogue.id);
      onNotice(`Cover regenerated for "${catalogue.name}"`);
      onChanged();
    } catch (e) { onError((e as Error).message); } finally { setBusy(null); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${catalogue.name}"? This removes the PDF, cover, and ${catalogue.position_count ?? 0} product positions.`)) return;
    setBusy('delete');
    try {
      await catalogueService.remove(catalogue.id);
      onNotice(`Deleted "${catalogue.name}"`);
      onChanged();
    } catch (e) { onError((e as Error).message); setBusy(null); }
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
      className="flex gap-4 rounded-lg border border-border bg-card p-4 shadow-sm"
    >
      {/* Cover */}
      <div className="relative flex h-32 w-24 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-muted">
        {catalogue.cover_url ? (
          <img src={catalogue.cover_url} alt={catalogue.name} className="h-full w-full object-cover"
            onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')} />
        ) : (
          <BookOpen className="h-7 w-7 text-muted-foreground" />
        )}
      </div>

      {/* Body */}
      <div className="min-w-0 flex-1">
        {editing ? (
          <div className="grid gap-2 sm:grid-cols-2">
            <div>
              <label className="text-xs text-muted-foreground">Name</label>
              <input value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} className={inputCls} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Brand</label>
              <input value={draft.brand} onChange={(e) => setDraft((d) => ({ ...d, brand: e.target.value }))} className={inputCls} />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs text-muted-foreground">Description</label>
              <input value={draft.description} onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))} className={inputCls} />
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <h3 className="truncate text-sm font-semibold text-foreground">{catalogue.name}</h3>
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <Badge variant="secondary">{catalogue.brand}</Badge>
              <Badge variant="outline" className="gap-1"><Layers className="h-3 w-3" />{catalogue.mapped_pages ?? 0} pages mapped</Badge>
              <Badge variant="outline" className="gap-1"><MapPin className="h-3 w-3" />{catalogue.position_count ?? 0} products</Badge>
              <Badge variant="outline" className="gap-1"><FileText className="h-3 w-3" />{fmtSize(catalogue.size_mb)}</Badge>
            </div>
            {catalogue.description && <p className="mt-1.5 line-clamp-2 text-xs text-muted-foreground">{catalogue.description}</p>}
            <a href={catalogue.url} target="_blank" rel="noreferrer" className="mt-1 inline-block text-xs text-primary hover:underline">Open PDF ↗</a>
          </>
        )}
      </div>

      {/* Actions */}
      <div className="flex shrink-0 flex-col items-end justify-between gap-2">
        <div className="flex items-center gap-1">
          <button type="button" className={btnGhostSm} disabled={isFirst} onClick={() => onMove(-1)} title="Move up"><ChevronUp className="h-4 w-4" /></button>
          <button type="button" className={btnGhostSm} disabled={isLast} onClick={() => onMove(1)} title="Move down"><ChevronDown className="h-4 w-4" /></button>
        </div>
        {editing ? (
          <div className="flex items-center gap-1.5">
            <button type="button" className={btnGhostSm} onClick={() => { setEditing(false); setDraft({ name: catalogue.name, brand: catalogue.brand, description: catalogue.description }); }} disabled={busy === 'save'}>Cancel</button>
            <button type="button" className={btnPrimarySm} onClick={save} disabled={busy === 'save'}>
              {busy === 'save' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Save
            </button>
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-end gap-1.5">
            <button type="button" className={btnOutlineSm} onClick={extractCover} disabled={busy === 'cover'} title="Render page 1 as the cover">
              {busy === 'cover' ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />} Cover
            </button>
            <ExtractDialog catalogue={catalogue} onDone={onNotice} onError={onError} />
            <button type="button" className={btnOutlineSm} onClick={() => setEditing(true)}><Pencil className="h-4 w-4" /> Edit</button>
            <button type="button" className={btnDangerSm} onClick={remove} disabled={busy === 'delete'}>
              {busy === 'delete' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
}

// ── Upload panel ─────────────────────────────────────────────────────────────────
function UploadPanel({ brands, onUploaded, onError }: {
  brands: BrandOption[];
  onUploaded: (msg: string) => void;
  onError: (msg: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [brand, setBrand] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const pickFile = (f: File | null) => {
    if (!f) return;
    if (f.type !== 'application/pdf' && !/\.pdf$/i.test(f.name)) return onError('Please choose a PDF file');
    setFile(f);
    if (!name) setName(f.name.replace(/\.pdf$/i, '').replace(/[_-]+/g, ' ').trim());
  };

  const reset = () => { setFile(null); setBrand(''); setName(''); setDescription(''); setOpen(false); };

  const submit = async () => {
    if (!file) return onError('Choose a PDF to upload');
    if (!brand.trim() || !name.trim()) return onError('Brand and name are required');
    setBusy(true);
    try {
      await catalogueService.upload({ file, brand, name, description });
      onUploaded(`Uploaded "${name}"`);
      reset();
    } catch (e) { onError((e as Error).message); } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button type="button" className={btnPrimary} onClick={() => setOpen(true)}>
        <Plus className="h-4 w-4" /> Upload catalogue
      </button>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
      className="rounded-lg border border-border bg-card p-4 shadow-sm"
    >
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground">Upload a new catalogue</h3>
        <button type="button" className={btnGhostSm} onClick={reset}><X className="h-4 w-4" /></button>
      </div>

      {/* Dropzone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); pickFile(e.dataTransfer.files?.[0] ?? null); }}
        onClick={() => inputRef.current?.click()}
        className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-4 py-8 text-center transition-colors ${dragOver ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`}
      >
        <UploadCloud className={`h-7 w-7 ${dragOver ? 'text-primary' : 'text-muted-foreground'}`} />
        {file ? (
          <div className="text-sm text-foreground">{file.name} <span className="text-muted-foreground">({fmtSize(file.size / (1024 * 1024))})</span></div>
        ) : (
          <div className="text-sm text-muted-foreground">Drop a PDF here, or click to browse</div>
        )}
        <input ref={inputRef} type="file" accept="application/pdf" className="hidden"
          onChange={(e) => pickFile(e.target.files?.[0] ?? null)} />
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <div>
          <label className="text-xs text-muted-foreground">Brand</label>
          {brands.length > 0 ? (
            <select value={brand} onChange={(e) => setBrand(e.target.value)} className={inputCls}>
              <option value="">Select a brand…</option>
              {brands.map((b) => <option key={b.slug} value={b.name}>{b.name}</option>)}
            </select>
          ) : (
            <input value={brand} onChange={(e) => setBrand(e.target.value)} className={inputCls} placeholder="Brand name" />
          )}
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Catalogue name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} placeholder="e.g. Räder — 2026" />
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs text-muted-foreground">Description (optional)</label>
          <input value={description} onChange={(e) => setDescription(e.target.value)} className={inputCls} />
        </div>
      </div>

      <div className="mt-4 flex items-center justify-end gap-2">
        <button type="button" className={btnOutline} onClick={reset} disabled={busy}>Cancel</button>
        <button type="button" className={btnPrimary} onClick={submit} disabled={busy || !file}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
          {busy ? 'Uploading…' : 'Upload'}
        </button>
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        After uploading, use <span className="text-foreground">Cover</span> to generate the thumbnail and
        <span className="text-foreground"> Map pages</span> to place products on catalogue pages.
      </p>
    </motion.div>
  );
}

// ── Panel ──────────────────────────────────────────────────────────────────────
export default function CataloguesPanel({ onError, onNotice }: {
  onError: (msg: string) => void;
  onNotice: (msg: string) => void;
}) {
  const [catalogues, setCatalogues] = useState<Catalogue[]>([]);
  const [brands, setBrands] = useState<BrandOption[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { catalogues, brands } = await catalogueService.list();
      setCatalogues(catalogues);
      setBrands(brands);
    } catch (e) { onError((e as Error).message); } finally { setLoading(false); }
  }, [onError]);

  useEffect(() => { load(); }, [load]);

  const move = async (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= catalogues.length) return;
    const reordered = [...catalogues];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    setCatalogues(reordered); // optimistic
    try {
      await catalogueService.reorder(reordered.map((c, i) => ({ id: c.id, sort_order: i })));
      load();
    } catch (e) { onError((e as Error).message); load(); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          PDF catalogues shown in the Trade Portal. Upload a PDF, generate its cover, and map products to pages.
        </p>
        <UploadPanel brands={brands} onUploaded={(m) => { onNotice(m); load(); }} onError={onError} />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : catalogues.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border py-16 text-center">
          <BookOpen className="mb-3 h-10 w-10 text-muted-foreground/50" />
          <p className="text-sm font-medium text-foreground">No catalogues yet</p>
          <p className="mt-1 text-sm text-muted-foreground">Upload a PDF to publish it to the Trade Portal.</p>
        </div>
      ) : (
        <div className="space-y-3">
          <AnimatePresence>
            {catalogues.map((c, i) => (
              <CatalogueCard
                key={c.id}
                catalogue={c}
                isFirst={i === 0}
                isLast={i === catalogues.length - 1}
                onChanged={load}
                onNotice={onNotice}
                onError={onError}
                onMove={(dir) => move(i, dir)}
              />
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
